"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveSkimPosition = resolveSkimPosition;
exports.skimWindowSeconds = skimWindowSeconds;
exports.totalSkimSeconds = totalSkimSeconds;
const utils_1 = require("@repo/utils");
/**
 * Map a position on the SKIM timeline — the keypoint windows laid end to end —
 * onto a keypoint and an absolute video time.
 *
 * The two timelines are not interchangeable and conflating them is what broke
 * the SkimHero scrub bar: a skim is typically ~10% of its video, so reading a
 * bar position as a skim fraction while seeking `fraction × videoDuration`
 * lands outside every keypoint window. The playback engine treats a seek that
 * lands outside the current window as "the user navigated away" and stops
 * sequential playback, which left the bar frozen with nothing driving it.
 *
 * `endMargin` keeps the resolved position clear of the window's end, so the
 * next 250ms tick doesn't immediately read `current >= endSec` and advance off
 * the keypoint the caller just asked for.
 *
 * Returns null when there are no keypoints to position against. Positions past
 * the end of the skim clamp to the last keypoint.
 */
function resolveSkimPosition(points, skimSeconds, endMargin = 0.5) {
    const list = points ?? [];
    if (list.length === 0)
        return null;
    // NaN only. `Infinity` must survive to the clamp below so an over-long
    // request lands at the END of the skim; rejecting all non-finite values sent
    // it to the START instead, which is the opposite of what was asked.
    let remaining = Number.isNaN(skimSeconds) ? 0 : Math.max(0, skimSeconds);
    for (let index = 0; index < list.length; index += 1) {
        const point = list[index];
        if (!point)
            continue;
        const startSec = (0, utils_1.timestampToSeconds)(point.startTimestamp);
        const endSec = (0, utils_1.timestampToSeconds)(point.endTimestamp);
        const duration = Math.max(0, endSec - startSec);
        const isLast = index === list.length - 1;
        if (remaining < duration || isLast) {
            const offset = Math.max(0, Math.min(remaining, duration - endMargin));
            return { index, offset, seconds: startSec + offset };
        }
        remaining -= duration;
    }
    return null;
}
/**
 * One keypoint window's length in seconds, measured the way the skim timeline
 * measures it: from its own timestamps.
 *
 * Deliberately NOT `totalDurationSeconds`. The two disagree by design —
 * `normalizeTimestampPair` keeps a timestamp pair whose span is within two
 * seconds of the backend's duration, so a window can span 116s while the field
 * reads 115. Anything that has to line up with a seek (a scrub bar's fill, its
 * denominator, an offset into a window) must use this one, because
 * `resolveSkimPosition` resolves against these same timestamps. Mixing the two
 * puts the bar on one timeline and the seek that reads it on another, and the
 * error accumulates with every keypoint. `totalDurationSeconds` stays the
 * backend's authoritative number for COPY — "9 min watch", "save 24 min".
 */
function skimWindowSeconds(point) {
    if (!point)
        return 0;
    return Math.max(0, (0, utils_1.timestampToSeconds)(point.endTimestamp) -
        (0, utils_1.timestampToSeconds)(point.startTimestamp));
}
/**
 * Total length of the skim timeline in seconds — the sum of every keypoint
 * window. This is the denominator the scrub bar's percentage is taken against;
 * it is NOT the video duration.
 */
function totalSkimSeconds(points) {
    return (points ?? []).reduce((sum, point) => sum + skimWindowSeconds(point), 0);
}
